import { useEffect, useRef, useState } from "react";
import { loadGoogleMaps, BW_MAP_STYLE } from "@/lib/googleMaps";

const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;

export default function MapCard({ card }) {
    const { center, zoom = 12, markers = [], route = [], city } = card;
    const containerRef = useRef(null);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!API_KEY) {
            setError("Google Maps API key is not configured.");
            return;
        }
        let cancelled = false;
        loadGoogleMaps(API_KEY)
            .then((g) => {
                if (cancelled || !containerRef.current) return;
                const map = new g.maps.Map(containerRef.current, {
                    center: { lat: center.lat, lng: center.lon },
                    zoom,
                    styles: BW_MAP_STYLE,
                    mapTypeControl: false,
                    streetViewControl: false,
                    fullscreenControl: false,
                });

                // Markers
                const info = new g.maps.InfoWindow();
                markers.forEach((m) => {
                    const marker = new g.maps.Marker({
                        position: { lat: m.lat, lng: m.lon },
                        map,
                        label: {
                            text: m.label,
                            color: "#FFFFFF",
                            fontSize: "11px",
                            fontWeight: "700",
                        },
                        icon: {
                            path: g.maps.SymbolPath.CIRCLE,
                            scale: 11,
                            fillColor: m.color || "#0A0A0A",
                            fillOpacity: 1,
                            strokeColor: "#FFFFFF",
                            strokeWeight: 2,
                        },
                    });
                    marker.addListener("click", () => {
                        info.setContent(
                            `<div style="font-family:Inter,sans-serif;font-size:12px;padding:4px 6px;">
                              <div style="font-weight:600;">${m.name}</div>
                              <div style="color:#737373;">Day ${m.day}</div>
                            </div>`,
                        );
                        info.open({ anchor: marker, map });
                    });
                });

                // Polylines per day
                route.forEach((r) => {
                    if (!r.path || r.path.length < 2) return;
                    new g.maps.Polyline({
                        path: r.path.map(([lat, lon]) => ({ lat, lng: lon })),
                        geodesic: true,
                        strokeColor: r.color || "#0A0A0A",
                        strokeOpacity: 0.85,
                        strokeWeight: 3,
                        map,
                    });
                });
            })
            .catch((e) => {
                console.error("[map] load failed", e);
                if (!cancelled) setError("Could not load the map. Please retry.");
            });
        return () => {
            cancelled = true;
        };
        // Initialize once per card instance — markers/route are immutable per response.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <article
            className="relative overflow-hidden rounded-2xl border border-[#E5E5E5] bg-white"
            data-testid="map-card"
        >
            <span
                aria-hidden
                className="absolute right-4 top-4 z-[5] inline-block h-2.5 w-2.5 rounded-full bg-[#0A0A0A] ring-4 ring-[#0A0A0A]/10"
            />
            <div className="border-b border-[#E5E5E5] px-4 py-3">
                <div className="text-[11px] uppercase tracking-wider text-[#A3A3A3]">
                    Route map
                </div>
                <div className="text-[15px] font-medium tracking-tight">
                    {city} · {markers.length} stops
                </div>
            </div>

            <div className="relative aspect-[16/9] w-full bg-[#F5F5F5]">
                <div
                    ref={containerRef}
                    className="absolute inset-0"
                    data-testid="map-canvas"
                />
                {error && (
                    <div
                        className="absolute inset-0 flex items-center justify-center px-4 text-center text-[13px] text-[#737373]"
                        data-testid="map-error"
                    >
                        {error}
                    </div>
                )}

                {/* Legend */}
                {route.length > 0 && (
                    <div
                        className="absolute bottom-3 left-3 rounded-lg border border-[#E5E5E5] bg-white/95 px-3 py-2 text-[11px] backdrop-blur"
                        data-testid="map-legend"
                    >
                        <div className="mb-1 text-[10px] uppercase tracking-wider text-[#A3A3A3]">
                            Days
                        </div>
                        <div className="flex flex-wrap gap-x-3 gap-y-1">
                            {route.map((r) => (
                                <div key={r.day} className="flex items-center gap-1.5">
                                    <span
                                        className="inline-block h-2.5 w-2.5 rounded-full"
                                        style={{ backgroundColor: r.color || "#0A0A0A" }}
                                    />
                                    Day {r.day}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </article>
    );
}
